import { hasInsightsFeature, hasHabitatInsightsFeature, isBasicProject } from 'src/shared/utils/featureHelpers';
import { getSectionErrors } from './sectionValidation';
import { NewProjectDefinitionFormState } from '../state/newProjectDefinitionSlice';

export type SectionStatus = {
    isComplete: boolean;
    hasErrors: boolean;
    isVisible: boolean;
    completedFields: number;
    totalFields: number;
};

export const getSectionStatus = (formState: NewProjectDefinitionFormState, section: string): SectionStatus => {
    const {
        projectName,
        country,
        projectLeadRole,
        projectLeadName,
        projectLeadEmail,
        features,
        sampleGroups,
        habitatAssayTypes,
        samplingEvents,
        hasDefinedSampleEvents,
    } = formState;
    const errors = getSectionErrors(formState, section as any);

    switch (section) {
        case 'projectDetails': {
            const requiredFields = [projectName, country, projectLeadRole];
            const optionalFields = [projectLeadName, projectLeadEmail];
            const totalFields = requiredFields.length + optionalFields.length;
            const completedFields = [...requiredFields, ...optionalFields].filter(Boolean).length;

            return {
                isComplete: errors.length === 0 && requiredFields.every(Boolean),
                hasErrors: errors.length > 0,
                isVisible: true,
                completedFields,
                totalFields,
            };
        }

        case 'projectAims': {
            const isVisible = hasInsightsFeature(features || []) || isBasicProject(features || []);
            if (!isVisible) {
                return {
                    isComplete: false,
                    hasErrors: false,
                    isVisible: false,
                    completedFields: 0,
                    totalFields: 0,
                };
            }

            // Count each field separately - sample groups and test types
            const hasSampleGroups = sampleGroups && sampleGroups.length > 0;
            const hasTestTypes = habitatAssayTypes && habitatAssayTypes.length > 0;
            const completedFields = (hasSampleGroups ? 1 : 0) + (hasTestTypes ? 1 : 0);

            return {
                isComplete: errors.length === 0,
                hasErrors: errors.length > 0,
                isVisible: true,
                completedFields,
                totalFields: 2, // Sample groups and test types
            };
        }

        case 'samplingCadence': {
            const isVisible = hasInsightsFeature(features || []) || isBasicProject(features || []);
            if (!isVisible) {
                return {
                    isComplete: false,
                    hasErrors: false,
                    isVisible: false,
                    completedFields: 0,
                    totalFields: 0,
                };
            }

            const hasInteracted = hasDefinedSampleEvents !== undefined;
            const hasEvents = samplingEvents && samplingEvents.length > 0;

            return {
                isComplete: errors.length === 0 && (!hasDefinedSampleEvents || hasEvents),
                hasErrors: errors.length > 0,
                isVisible: true,
                completedFields: hasInteracted ? 1 : 0,
                totalFields: 1,
            };
        }

        case 'habitatInsights': {
            const isVisible = hasHabitatInsightsFeature(features || []);
            return {
                isComplete: false, // Habitat insights section completion logic would go here
                hasErrors: false,
                isVisible,
                completedFields: 0,
                totalFields: 0,
            };
        }

        default:
            return {
                isComplete: false,
                hasErrors: false,
                isVisible: false,
                completedFields: 0,
                totalFields: 0,
            };
    }
};

export const getCurrentSectionIndex = (formState: NewProjectDefinitionFormState): number => {
    const sections = ['projectDetails', 'projectAims', 'samplingCadence', 'habitatInsights', 'review'];

    // Find first incomplete section
    for (let i = 0; i < sections.length; i++) {
        const status = getSectionStatus(formState, sections[i]);
        if (status.isVisible && !status.isComplete) {
            return i;
        }
    }

    // All sections complete, return review section
    return sections.length - 1;
};
